import { useContext, useState } from "react";
import { apiRequest } from "../../api/baseApi";
import Layout from "../../components/layout/Layout";
import { useSyncSelectedClient } from "../../Hooks/useSyncSelected";
import { AuthContext } from "../../state/AuthContext";
import { ClientContext } from "../../state/ClientContext";
import { toTitleCase } from "../../util/toTitleCase";
import CaregiverAssignments from "./CaregiverAssignments";
import CaregiverDetailsForm from "./CaregiverDetailsForm";
import CommandCenterAccessToggle from "../../components/commandCenter/CommandCenterAccessToggle";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationModal from "../../components/modals/DeleteConfirmationModal";

const CaregiverProfilePage = () => {
  const {
    selectedCaregiver,
    deleteCaregiver,
    isDeletingCaregiver,
    refetchCaregivers,
  } = useContext(ClientContext);
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useSyncSelectedClient();

  const handleDelete = async () => {
    if (!selectedCaregiver?.id) return;

    try {
      await deleteCaregiver(selectedCaregiver.id);
      setShowDeleteModal(false);
      navigate("/select-client-homepage");
    } catch (error) {
      console.error("Error deleting caregiver:", error);
    }
  };

  const handleResendInvite = async (e) => {
    e.preventDefault();
    try {
      const response = await apiRequest("/users/resendInvite", {
        method: "POST",
        body: { email: selectedCaregiver.email },
        version: "v1",
      });
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
      } else {
        console.error("Failed to resend invite");
      }
    } catch (error) {
      console.error("Error resend invite", error);
    }
  };

  if (!selectedCaregiver) {
    return null;
  }

  return (
    <Layout containerClassName="bg-page-light" showSideNav={false}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginLeft: "40px",
          paddingTop: "20px",
        }}
      >
        <h1 style={{ fontWeight: 800, margin: 0 }}>
          <span style={{ fontSize: "2.4rem", color: "#222" }}>
            {toTitleCase(selectedCaregiver.name)}
          </span>
        </h1>

        {selectedCaregiver.isActive ? (
          <div
            style={{
              backgroundColor: "#ACAF81",
              color: "#32693A",
              fontSize: "1rem",
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            Registered
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              fontSize: "1rem",
              gap: "12px",
              alignItems: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "#ACAF81",
                color: "#FE0909",
                padding: "4px 8px",
                borderRadius: "4px",
              }}
            >
              Unregistered
            </div>
            <a
              href="#"
              onClick={handleResendInvite}
              style={{
                color: "#007BFF",
                textDecoration: "underline",
                marginTop: "2px",
                fontSize: "0.9rem",
              }}
            >
              Resend Invite
            </a>
          </div>
        )}
      </div>
      <div
        data-testid="caregiver-profile-columns"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          padding: "16px",
        }}
      >
        <div style={{ flex: "1 1 480px", minWidth: 0 }}>
          <CaregiverDetailsForm />
          {/* #775: the admin decides whether THIS caregiver gets Command
              Center. The component renders nothing for a non-admin, and the
              server authorizes every write regardless — the role check here
              is only so a caregiver is not shown a control they cannot use.

              `key` is load-bearing. This route stays mounted when
              :caregiverId changes, so without it the switch would keep the
              previous caregiver's value — and an in-flight save for the
              previous caregiver could settle into this one's UI. Keying on the
              id remounts the control with fresh state and orphans that reply.

              onChange refetches the roster because `selectedCaregiver` is
              derived from ClientContext's cached caregivers list. Leaving the
              switch's own state as the only record of the change would show
              the pre-save value again on the next visit. */}
          <CommandCenterAccessToggle
            key={selectedCaregiver.id}
            caregiverId={selectedCaregiver.id}
            caregiverName={toTitleCase(selectedCaregiver.name)}
            enabled={selectedCaregiver.commandCenterEnabled}
            canManage={Boolean(
              currentUser?.isSuperAdmin || currentUser?.isAdmin,
            )}
            onChange={() => {
              // Not passed directly: onChange hands over the confirmed
              // boolean, and refetch() takes no arguments today. Forwarding it
              // would go unnoticed the day that changes.
              refetchCaregivers();
            }}
          />
          {(currentUser?.isSuperAdmin || currentUser?.isAdmin) && (
            <button
              className={`delete-client btn-danger`}
              onClick={() => setShowDeleteModal(true)}
            >
              Delete caregiver
            </button>
          )}
        </div>

        <div style={{ flex: "1 1 480px", minWidth: 0 }}>
          <CaregiverAssignments />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        isLoading={isDeletingCaregiver}
        itemName={selectedCaregiver.name}
        itemType="Caregiver"
        warningMessage="All associated data and assignments will be permanently removed."
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </Layout>
  );
};

export default CaregiverProfilePage;
